import { z } from 'zod';

/**
 * Combos (العروض) — a fixed bundle of existing items sold at one special price.
 *
 * The combo IS an ordinary item (`items.is_combo`), so its price, name, images
 * and publish state live where every other item's do; this file only describes
 * what is INSIDE the box. Components carry the unit snapshot trio, so "2 packs
 * of 12" means 24 base units come off the shelf, exactly like a document line.
 * Quantities are 4-dp decimal STRINGS end to end, the codebase convention.
 */

const positiveDecimalString = z
  .string()
  .regex(/^\d+(\.\d{1,4})?$/)
  .refine((v) => Number(v) > 0, 'must be > 0');

export const ComboLineInputSchema = z.object({
  componentItemId: z.string().uuid(),
  /** Unit snapshot trio; base taken from stock = quantity × unitFactor. */
  itemUnitId: z.string().uuid().nullable().optional(),
  unitName: z.string().max(64).nullable().optional(),
  unitFactor: positiveDecimalString.default('1'),
  /** How many of this component are in ONE combo, in the chosen unit. */
  quantity: positiveDecimalString,
});
export type ComboLineInput = z.infer<typeof ComboLineInputSchema>;

/** Replaces the whole component list — the editor edits a bundle as a whole. */
export const ComboUpsertSchema = z.object({
  lines: z.array(ComboLineInputSchema).min(1).max(100),
});
export type ComboUpsert = z.infer<typeof ComboUpsertSchema>;

export interface ComboLine {
  id: string;
  lineNo: number;
  componentItemId: string;
  componentCode: string;
  componentName: string;
  componentUom: string;
  itemUnitId: string | null;
  unitName: string | null;
  unitFactor: string;
  quantity: string;
  /**
   * The component's own shelf price, so the editor can show what the parts
   * would cost bought separately (and therefore what the bundle saves).
   * Null when the item has no price in the combo's currency — the editor says
   * so rather than pretending the saving is bigger than it is.
   */
  unitSalePrice: string | null;
  saleCurrencyCode: string | null;
  /** Base units currently on hand across all warehouses. */
  onHand: string;
}

export interface Combo {
  itemId: string;
  itemCode: string;
  itemName: string;
  /** The bundle's own price, echoed so the editor can compute the saving. */
  salePrice: string | null;
  saleCurrencyCode: string | null;
  lines: ComboLine[];
}

export interface ComboSummary {
  itemId: string;
  itemCode: string;
  itemName: string;
  salePrice: string | null;
  saleCurrencyCode: string | null;
  componentCount: number;
  isActive: boolean;
  /**
   * How many whole combos the components on hand could cover — the honest
   * answer to "can I still sell this?", since the bundle has no stock of its own.
   */
  buildable: string;
}
